import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAutoPaginationNavigation } from '@/utils/autoPaginationNavigation';
import {
  Box,
  CircularProgress,
  Alert,
  Link,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  GetApp as DownloadIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import Images from '@/assets/Images';
import styles from '../AssetManagement/AssetDetails/AssetDetails.module.scss';
import { useGetScheduleByIdQuery } from '../../store/api/scheduleApi';
import { handleDownload, handlePreview } from '@/services/documentService';
import { ScheduleStatus, getScheduleStatusLabel } from '../../enums/schedule.enums';
import { useCurrency } from '@/components/common/CurrencyFormat';
import { formatDate as formatDateUtil } from '@/utils/formatters';
const ScheduleDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { backToListing } = useAutoPaginationNavigation(navigate, '/schedule-management/schedules');
  const { data: schedule, isLoading, error } = useGetScheduleByIdQuery(id || '');
  
  const currencySymbol = useCurrency().currencySymbol;
  // Format date function
  const formatDate = (dateString?: string) => {
    if (!dateString) return '--';
    return formatDateUtil(dateString, 'M/d/yyyy', dateString);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="appWrapper">
        <h3 className="pageTitle">
          <Link onClick={() => backToListing()} className="back">
            <img src={Images.BACK} alt="Back" width="22" />
          </Link>
          Schedule View
        </h3>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  // Error state
  if (error || !schedule) {
    return (
      <div className="appWrapper">
        <h3 className="pageTitle">
          <Link onClick={() => backToListing()} className="back">
            <img src={Images.BACK} alt="Back" width="22" />
          </Link>
          Schedule View
        </h3>
        <Alert severity="error">
          Error loading schedule details. The schedule may not exist or you may not have permission to view it.
        </Alert>
      </div>
    );
  }

  return (
    <div className="appWrapper">
      <h3 className="pageTitle">
        <Link onClick={() => backToListing()} className="back">
          <img src={Images.BACK} alt="Back" width="22" />
        </Link>
        Schedule View
      </h3>
      

      <div className="appGridContainer">
        <div className="gridWrapper">
          <div className={`${styles.assetDetWrapper}`}>
            <div className={`${styles.assetDetContainer} bordered r10`}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <h4>
                  {schedule.name}
                </h4>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <span className={`status ${schedule.status === ScheduleStatus.ACTIVE ? 'active' : 'inactive'}`}>
                    {getScheduleStatusLabel(schedule.status)}
                  </span>
                </div>
              </div>

              <section className={`border_bottom ${styles.assetBasic}`}>
                <div className="d_flex">
                  <div className="col">
                    <label className="label">Activity Type</label>
                    <h5 className="body1">
                      {schedule.activity_type?.name || '--'}
                    </h5>
                  </div>
                  <div className="col">
                    <label className="label">Asset</label>
                    <h5 className="body1">
                      {schedule.asset
                        ? `${schedule.asset.asset_name} (${schedule.asset.asset_tag})`
                        : '--'}
                    </h5>
                  </div>
                  <div className="col">
                    <label className="label">Location</label>
                    <h5 className="body1">
                      {schedule.location?.location_hierarchy || '--'}
                    </h5>
                  </div>
                  <div className="col">
                    <label className="label">User Group</label>
                    <h5 className="body1">
                      {schedule.user_group?.name || '--'}
                    </h5>
                    {schedule.user_group?.users_detail && schedule.user_group.users_detail.length > 0 && (
                      <div style={{ marginTop: '5px' }}>
                        <label className="label" style={{ fontSize: '12px', color: '#666' }}>
                          Members ({schedule.user_group.users_detail.length}):
                        </label>
                        <div style={{ marginTop: '3px' }}>
                          {schedule.user_group.users_detail.map((user: any, index: number) => (
                            <span key={user.id} style={{
                              fontSize: '13px',
                              color: '#555',
                              display: 'inline-block',
                              marginRight: '8px',
                              marginBottom: '2px'
                            }}>
                              {user.name}
                              {index < schedule.user_group.users_detail.length - 1 && ','}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              <section>
                <div className="d_flex wrap">
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Start Date</label>
                    <h5 className="body1">
                      {formatDate(schedule.start_date)}
                    </h5>
                  </div>
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">End Date</label>
                    <h5 className="body1">
                      {formatDate(schedule.end_date)}
                    </h5>
                  </div>
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Frequency</label>
                    <h5 className="body1">
                      {schedule.frequency_type && schedule.frequency
                        ? `Every ${schedule.frequency} ${schedule.frequency_type}${schedule.frequency > 1 ? 's' : ''}`
                        : (schedule.frequency ? `${schedule.frequency} days` : '--')}
                    </h5>
                  </div>

                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Checklist Template</label>
                    <h5 className="body1">
                      {schedule.checklist_template?.name || '--'}
                    </h5>
                  </div>
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Cost Template</label>
                    <h5 className="body1">
                      {schedule.cost_template?.name || '--'}
                    </h5>
                  </div>
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Description</label>
                    <h5 className="body1">
                      {schedule.description || '--'}
                    </h5>
                  </div>
                </div>
              </section>

              <section className="border_bottom pb_30 pt_30">
                <div className="d_flex wrap">
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Execution Notification</label>
                    <h5 className="body1">
                      {schedule.execution_before ? `${schedule.execution_before} days before` : '--'}
                    </h5>
                  </div>
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Reminder Notification</label>
                    <h5 className="body1">
                      {schedule.reminder_before ? `${schedule.reminder_before} days before` : '--'}
                    </h5>
                  </div>
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Status</label>
                    <h5 className="body1">{getScheduleStatusLabel(schedule.status)}</h5>
                  </div>
                </div>
              </section>


              {/* Asset Details Section */}
              {schedule.asset && (
                <section className="border_bottom pb_30 pt_30">
                  <h4 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                    Asset Details
                  </h4>
                  <div className="d_flex wrap">
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Asset Tag</label>
                      <h5 className="body1">{schedule.asset.asset_tag || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Asset Name</label>
                      <h5 className="body1">{schedule.asset.asset_name || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Asset Category</label>
                      <h5 className="body1">{schedule.asset.asset_category_detail?.name || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Asset Status</label>
                      <h5 className="body1">{schedule.asset.asset_status_detail?.name || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Asset Condition</label>
                      <h5 className="body1">{schedule.asset.asset_condition_detail?.name || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Serial Number</label>
                      <h5 className="body1">{schedule.asset.serial_number || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Current Value</label>
                      <h5 className="body1">
                        {schedule.asset.current_value ? `${currencySymbol}${schedule.asset.current_value}` : '--'}
                      </h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Purchase Value</label>
                      <h5 className="body1">
                        {schedule.asset.purchase_value ? `${currencySymbol}${schedule.asset.purchase_value}` : '--'}
                      </h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Total Expense</label>
                      <h5 className="body1">
                        {schedule.asset.total_expense ? `${currencySymbol}${schedule.asset.total_expense}` : '--'}
                      </h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Assigned To</label>
                      <h5 className="body1">{schedule.asset.assigned_to_detail?.name || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Assigned Email</label>
                      <h5 className="body1">{schedule.asset.assigned_to_detail?.email || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Department</label>
                      <h5 className="body1">{schedule.asset.department_detail?.name || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Manufacturer</label>
                      <h5 className="body1">{schedule.asset.manufacturer_detail?.name || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Model</label>
                      <h5 className="body1">{schedule.asset.model_detail?.name || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Purchase Date</label>
                      <h5 className="body1">{formatDate(schedule.asset.purchase_date)}</h5>
                    </div>
                  </div>
                </section>
              )}

              {/* Location Details Section */}
              {schedule.location && (
                <section className="border_bottom pb_30 pt_30">
                  <h4 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                    Location Details
                  </h4>
                  <div className="d_flex wrap">
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Location Name</label>
                      <h5 className="body1">{schedule.location.name || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Location Type</label>
                      <h5 className="body1">{schedule.location.location_type || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Location Hierarchy</label>
                      <h5 className="body1">{schedule.location.location_hierarchy || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Site Name</label>
                      <h5 className="body1">{schedule.location.site_detail?.name || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">City</label>
                      <h5 className="body1">{schedule.location.site_detail?.city || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">State</label>
                      <h5 className="body1">{schedule.location.site_detail?.state_detail?.name || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Country</label>
                      <h5 className="body1">{schedule.location.site_detail?.country_detail?.name || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Contact Person</label>
                      <h5 className="body1">{schedule.location.contact_person_detail?.name || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Contact Email</label>
                      <h5 className="body1">{schedule.location.contact_person_detail?.email || '--'}</h5>
                    </div>
                  </div>
                </section>
              )}

              {/* Activity Type Details Section */}
              {schedule.activity_type && (
                <section className="border_bottom pb_30 pt_30">
                  <h4 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                    Activity Type Details
                  </h4>
                  <div className="d_flex wrap">
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Activity Type Name</label>
                      <h5 className="body1">{schedule.activity_type.name || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Description</label>
                      <h5 className="body1">{schedule.activity_type.description || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Payment Required</label>
                      <h5 className="body1">{schedule.activity_type.payment_required ? 'Yes' : 'No'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Checklist Required</label>
                      <h5 className="body1">{schedule.activity_type.checklist_required ? 'Yes' : 'No'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">File Upload Required</label>
                      <h5 className="body1">{schedule.activity_type.file_upload_required ? 'Yes' : 'No'}</h5>
                    </div>
                  </div>
                </section>
              )}

              {/* Template Details Section */}
              <section className="pb_30 pt_30">
                <h4 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                  Template Details
                </h4>
                <div className="d_flex wrap">
                  <div className="col_50 mt_10 mb_10">
                    <label className="label">Checklist Template</label>
                    <h5 className="body1">{schedule.checklist_template?.name || '--'}</h5>
                    {schedule.checklist_template?.items && schedule.checklist_template.items.length > 0 && (
                      <div style={{ marginTop: '10px' }}>
                        <label className="label" style={{ fontSize: '12px', color: '#666' }}>
                          Questions ({schedule.checklist_template.items.length}):
                        </label>
                        <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                          {schedule.checklist_template.items.map((item: any, index: number) => (
                            <li key={item.id} style={{ fontSize: '14px', marginBottom: '5px' }}>
                              {item.question}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="col_50 mt_10 mb_10">
                    <label className="label">Cost Template</label>
                    <h5 className="body1">{schedule.cost_template?.name || '--'}</h5>
                    {schedule.cost_template?.items && schedule.cost_template.items.length > 0 && (
                      <div style={{ marginTop: '10px' }}>
                        <label className="label" style={{ fontSize: '12px', color: '#666' }}>
                          Cost Items ({schedule.cost_template.items.length}):
                        </label>
                        <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                          {schedule.cost_template.items.map((item: any, index: number) => (
                            <li key={item.id} style={{ fontSize: '14px', marginBottom: '5px' }}>
                              {item.label} {item.is_mandatory && <span style={{ color: 'red' }}>*</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* Attachments Section */}
              {schedule.documents_detail && schedule.documents_detail.length > 0 && (
                <section className="pb_30 pt_30">
                    <div className="d_flex wrap">
                      <div className="col_100 mt_10 mb_10">
                        <label className="label">
                          <AttachFileIcon sx={{ fontSize: 18, verticalAlign: 'text-bottom', mr: 0.5 }} />
                          Attachments ({schedule.documents_detail.length})
                        </label>
                        <div style={{ marginTop: '10px' }}>
                          {schedule.documents_detail.map((attachment) => (
                            <div key={attachment.id} style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px',
                              border: '1px solid #e0e0e0',
                              borderRadius: '4px',
                              marginBottom: '8px',
                              backgroundColor: '#f9f9f9'
                            }}>
                              <AttachFileIcon sx={{ mr: 1 }} />
                              <div style={{ flexGrow: 1 }}>
                                <Typography variant="body2">
                                  {attachment?.filename}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {(attachment?.file_size / 1024).toFixed(2)} KB
                                </Typography>
                              </div>
                              <Tooltip title="View">
                                <IconButton size="small" onClick={() => handlePreview(attachment)}>
                                  <PreviewIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download">
                                <IconButton size="small" onClick={() => handleDownload(attachment)}>
                                  <DownloadIcon />
                                </IconButton>
                              </Tooltip>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </section>
              
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleDetailPage;